import type { MaturityItemId } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import type { TFunction } from '@cobliteam/i18n';
import type { CursorFlowStep } from '../../store/useCursorFlowStore';

/** Leaflet/Google map container on the geofences create page (origin for map-relative steps). */
const GEOFENCE_MAP_SELECTOR = '#geofence-map-google-layer';

/**
 * CSS selector of the primary action to guide the cursor to on each item's destination page.
 * Items without a stable page-level target (device install/association, per-road speed) are omitted
 * — they navigate without a cursor flow. Geofences gets a full simulated walkthrough below.
 */
const ITEM_TARGET_SELECTOR: Partial<Record<MaturityItemId, string>> = {
  vehicles: "[data-testid='add-vehicle-button-dropdown']",
  drivers: "[data-testid='add-button-driver-page']",
  users: "[data-testid='invite-users-button']",
  groups: "[data-testid='add-group-button']",
  alerts: "[data-testid='save-trigger-button']",
  checklists: "[data-testid='create-checklist-button']",
  // 'driver-association' has a richer walkthrough (see buildItemTourSteps), not a single pulse.
};

/** First driver row without an associated vehicle (the "---" / "associate" cell), and that row's 3-dots button. */
const NO_VEHICLE_CELL = "[data-tour='driver-no-vehicle']";
const NO_VEHICLE_ROW_ACTIONS = "tr:has([data-tour='driver-no-vehicle']) [data-tour='drivers-table-action-button']";
const ASSOCIATE_MENU_ITEM = "[data-tour='associate-vehicle-menu-item']";

/**
 * Driver↔vehicle association walkthrough on the drivers page: find a driver with no vehicle, circle
 * the empty "---" cell, open that row's actions (real click reveals the menu) and click "Associate
 * vehicle" (real click opens the dialog). Shared by the Basic Value page and the guided onboarding modal.
 */
export const buildDriverAssociationTourSteps = (t: TFunction): CursorFlowStep[] => [
  { selector: NO_VEHICLE_CELL, label: t('tour_driver_assoc.find'), holdMs: 600 },
  { originSelector: NO_VEHICLE_CELL, orbit: { radius: 26, turns: 2 }, label: t('tour_driver_assoc.circle'), holdMs: 1500 },
  { selector: NO_VEHICLE_ROW_ACTIONS, action: 'click', realClick: true, label: t('tour_driver_assoc.actions'), holdMs: 900 },
  { selector: ASSOCIATE_MENU_ITEM, action: 'click', realClick: true, label: t('tour_driver_assoc.associate'), holdMs: 1000 },
];

/**
 * Builds the guided cursor-flow choreography for a maturity item, played after the page navigates
 * to the item's destination. Geofences gets a full walkthrough (type name → pick circle → draw on
 * the map → choose type → save); every other item gets a single guiding pulse on its target button.
 * Returns null when the item has no stable on-page target.
 */
export const buildItemTourSteps = (itemId: MaturityItemId, t: TFunction): CursorFlowStep[] | null => {
  if (itemId === 'geofences') {
    return [
      { selector: 'input[name="name"]', action: 'type', text: t('tour_geofence.sample_name'), label: t('tour_geofence.name'), holdMs: 500 },
      { selector: '[data-tour="geofence-format-CIRCLE"]', action: 'click', label: t('tour_geofence.circle'), holdMs: 500 },
      { originSelector: GEOFENCE_MAP_SELECTOR, offset: { x: 0, y: 0 }, action: 'click', label: t('tour_geofence.center'), holdMs: 300 },
      { originSelector: GEOFENCE_MAP_SELECTOR, offset: { x: 70, y: -42 }, dragCircle: true, action: 'finish', label: t('tour_geofence.radius'), holdMs: 500 },
      { selector: '[data-tour="geofence-type-CLIENT"]', action: 'click', label: t('tour_geofence.type'), holdMs: 500 },
      { selector: '#submitGeofenceForm', action: 'click', label: t('tour_geofence.save'), holdMs: 800 },
    ];
  }

  if (itemId === 'driver-association') {
    return buildDriverAssociationTourSteps(t);
  }

  const selector = ITEM_TARGET_SELECTOR[itemId];
  return selector ? [{ selector, action: 'click', label: t('tour.hint') }] : null;
};
